<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :return-value.sync="currentDate"
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template v-slot:activator="{ on, attrs }">
      <div v-if="mode == 'input'">
        <v-text-field
          :id="id"
          v-model="datePresentation"
          readonly
          v-bind="attrs"
          v-on="on"
        >
          <template v-slot:prepend-inner>
            <v-btn
              icon
              small
              color="primary"
              elevation="0"
              @click="menu = true"
            >
              <v-icon> bhi-calendar </v-icon>
            </v-btn>
          </template>
        </v-text-field>
        <label :for="id" v-if="label">{{ label }}</label>
      </div>
      <div v-if="mode == 'select'">
        <v-btn color="accent" elevation="0" rounded v-on="on" v-bind="attrs"
          >{{ selected || 'Last week' }}
          <v-icon class="ml-2">bhi-chevron-down</v-icon></v-btn
        >
      </div>
    </template>
    <date-range-picker
      v-model="range"
      :opens="'inline'"
      :minDate="minDate"
      :maxDate="maxDate"
      :autoApply="true"
      :ranges="ranges"
      @update="handleInput"
      @start-selection="dayClick"
      @select="selectRange"
    />
  </v-menu>
</template>
<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
let today = new Date()
const dd = String(today.getDate()).padStart(2, '0')
const mm = String(today.getMonth() + 1).padStart(2, '0')
const yyyy = today.getFullYear()
today = `${yyyy}-${mm}-${dd}`

export default {
  name: 'DateRangeCalendarsPicker',
  components: {
    DateRangePicker,
  },
  props: {
    date: {
      type: [Date, String],
      default: () => '',
    },
    label: {
      type: String,
      default: '',
    },
    id: {
      type: [Number, String],
      default: 'datePicker',
    },
    mode: {
      type: String,
      default: 'input',
    },
    initialEndDate: {
      type: [Date, String],
      default: () => '',
    },
    initialStartDate: {
      type: [Date, String],
      default: () => '',
    },
    limitSelectedDays: {
      type: Number,
      default: 30,
    },
    canBeEmpty: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      range: {
        startDate: today,
        endDate: today,
      },
      menu: false,
      setRange: null,
      currentDate: this.date,
      today,
      selected: false,
      startDate: null,
      minDate: null,
      maxDate: today,
    }
  },
  mounted() {
    let endDate = today
    if (this.$props.initialEndDate) {
      endDate = this.$moment(this.$props.initialEndDate).format('YYYY-MM-DD')
    }
    let startDate = this.$moment(endDate)
      .subtract(1, 'week')
      .add(1, 'day')
      .format('YYYY-MM-DD')
    if (this.$props.initialStartDate) {
      startDate = this.$moment(this.$props.initialStartDate).format(
        'YYYY-MM-DD',
      )
    }
    if (this.$props.canBeEmpty) {
      if (!this.$props.initialStartDate) {
        startDate = null
      }
      if (!this.$props.initialEndDate) {
        endDate = null
      }
    }
    this.range.startDate = startDate
    this.range.endDate = endDate
    this.setLabel(this.range)
  },
  watch: {
    date(val) {
      if (this.$moment(val).format('YYYY-MM-DD') != this.currentDate)
        this.currentDate = this.$moment(val).format('YYYY-MM-DD')
    },
  },
  computed: {
    datePresentation() {
      return this.setRange
        ? this.$moment(this.range.startDate).format('MM/DD/YYYY') +
            ' - ' +
            this.$moment(this.range.endDate).format('MM/DD/YYYY')
        : 'Choose date'
    },
    todayDate() {
      return this.$moment(today).toDate()
    },
    lastWeek() {
      return this.$moment(today)
        .subtract(1, 'week')
        .add(1, 'day')
        .toDate()
    },
    last2Weeks() {
      return this.$moment(today)
        .subtract(2, 'weeks')
        .add(1, 'day')
        .toDate()
    },
    lastMonth() {
      return this.$moment(today)
        .subtract(30, 'days')
        .toDate()
    },
    ranges() {
      const ranges = {
        'Last week': [this.lastWeek, this.todayDate],
        'Last 2 weeks': [this.last2Weeks, this.todayDate],
        Month: [this.lastMonth, this.todayDate],
      }
      if (this.canBeEmpty) {
        ranges['All dates'] = [null, null]
      }
      return {
        ...ranges,
      }
    },
  },
  methods: {
    onConfirm() {
      this.$refs.menu.save(this.range)
      this.setRange = this.range
      this.setLabel(this.range)
      this.$emit(
        'input',
        this.$moment(this.range.startDate).format('YYYY-MM-DD'),
        this.$moment(this.range.endDate).format('YYYY-MM-DD'),
      )
    },
    handleInput(inputDates) {
      // max 30 days, so if someone selects old date and then moves to newer date, we need to allow to select only 30 days from the start date
      if (
        this.$moment(inputDates.startDate).isBefore(
          this.$moment(inputDates.endDate).subtract(
            this.limitSelectedDays,
            'days',
          ),
        )
      ) {
        inputDates.endDate = this.$moment(inputDates.startDate)
          .add(this.limitSelectedDays, 'days')
          .toDate()
        confirm(
          `Only ${
            this.limitSelectedDays
          } days are allowed, end date was set to ${this.$moment(
            inputDates.endDate,
          ).format('MM/DD/YYYY')}`,
        )
      }
      this.range = inputDates
      this.onConfirm()
    },
    dayClick(day) {
      this.startDate = day
      this.minDate = this.$moment(day)
        .subtract(this.limitSelectedDays, 'days')
        .format('YYYY-MM-DD')
    },
    selectRange(range) {
      this.setLabel(range)
      this.range = range
      this.$emit('select-range', this.range)
    },
    setLabel(range) {
      if (!range.startDate && !range.endDate) {
        label = 'All dates'
        this.selected = label
        return
      }
      let diff = -this.$moment
        .duration(
          this.$moment(range.startDate).diff(this.$moment(range.endDate)),
        )
        .asDays()
      let label =
        this.$moment(range.startDate).format('MM/DD/YYYY') +
        ' - ' +
        this.$moment(range.endDate).format('MM/DD/YYYY')
      if (this.$moment(range.endDate).isSame(this.$moment(this.today), 'day')) {
        switch (diff) {
          case 6:
            label = 'Last week'
            break
          case 13:
            label = 'Last 2 weeks'
            break
          case 30:
            label = 'Last month'
            break
        }
      }
      this.selected = label
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.vue-daterange-picker {
  .daterangepicker {
    margin-top: 0 !important;
  }
  .daterangepicker td.active,
  .daterangepicker td.active:hover {
    background-color: $bh-blue;
  }
  .daterangepicker td.off.in-range {
    background-color: #f1f7f9;
  }
  .daterangepicker td.off.active {
    background-color: #8dd7f5;
    color: #ffffff;
  }
  .reportrange-text {
    display: none;
  }
  .calendars {
    display: flex;
    flex-direction: row-reverse;
  }
  .daterangepicker .ranges li.active {
    background-color: $bh-blue;
  }
}
</style>
